/**
 * ABYSS modular habitat kit — the game meshes.
 *
 * `docs/HABITAT.md` §2.4: Fusion is the DIMENSION authority, the meshes are built
 * here. Every number below is the Fusion user parameter in metres, and
 * `docs/HABITAT-FUSION.md` records where the CAD model and this file must agree.
 *
 * Axes are the engine's, so the Fusion mapping (§4) is already applied:
 *
 *     fusion.x -> x   longitudinal, along the world
 *     fusion.z -> y   up
 *    -fusion.y -> z   toward the camera
 *
 * Which means a module's cross-section lives in the ZY plane, `hy` is its half
 * height and `hz` its half depth along the camera axis. The camera sits on +Z,
 * so +Z is the side that gets the windows and −Z is the side that is never
 * rasterised (§2.1).
 *
 * `uv.x` carries height up the module, 0 at the keel and 1 at the roof (§6.4).
 * It is the one free per-vertex float and the convention is fixed here.
 */

import { MeshBuilder, MAT, VERT_FLOATS, box, cylinderY, type MeshData } from './mesh';

/**
 * PER-VERTEX COLOUR AND AMBIENT OCCLUSION — the thing that was missing.
 *
 * The boat does not look like the boat because of its triangles. It looks like
 * it because every vertex carries its OWN albedo and its OWN occlusion, baked
 * from the CAD materials: `subModel2.ts:104` packs three bytes into `uv.x` as
 * `r*65536 + g*256 + b`, puts AO in `uv.y`, and sets `params2.w = 1` to switch
 * the whole path on. The habitat was a single flat per-INSTANCE tint, which is
 * why no amount of geometry stopped it reading as one moulded plastic shape.
 *
 * NOT CURRENTLY SWITCHED ON — see the note in `World.drawHabitats`. `params2.w`
 * is a bundle, and the rivet generator that comes with it is sized for the boat.
 * The packing below is kept because it is correct and because the moment the
 * shader grows a way to ask for colour WITHOUT `onHull`, this is one flag away.
 *
 * `params2.w` unlocks three things together (see `vsMesh`):
 *   o.base = ALBEDO_FLOOR + uv.x/255 * ALBEDO_SPAN   per-vertex albedo
 *   o.ao   = uv.y                                     per-vertex occlusion
 *   o.riv  = local position                           the MARINE PAINT detail
 *                                                     path, not the rock one
 *
 * That third one matters as much as the first: without it `MAT.paint` at 0.0
 * selects layer 0, which is `rock-detail.png`, and a habitat gets shaded as
 * though it were a boulder.
 *
 * Colours are 0..255 bytes remapped by the shader into 0.055..0.82 — "real
 * paint runs about 0.05 to 0.82", so anything outside that has no headroom to
 * be shaded or no floor to catch a highlight.
 */
type Rgb = [number, number, number];
const PACK = (c: Rgb) => c[0] * 65536 + c[1] * 256 + c[2];

const SKIN = {
  /** Graphite body, as both reference sheets draw it. */
  paint: [46, 52, 60] as Rgb,
  /** Brushed steel: bright enough that the lamp has something to land on. */
  metal: [148, 156, 166] as Rgb,
  rubber: [30, 30, 34] as Rgb,
  glow: [255, 255, 255] as Rgb,
};

/**
 * Walk a built mesh and give every vertex a colour and an occlusion.
 *
 * Done as a post-pass rather than at each `vertex()` call because `box`,
 * `cylinderY` and `revolveX` write their own uv, and rewriting all of them to
 * carry a palette would be a much larger change for the same result.
 */
function skin(d: MeshData, hy: number): MeshData {
  const v = d.vertices;
  for (let i = 0; i < v.length; i += VERT_FLOATS) {
    const y = v[i + 1]!, ny = v[i + 4]!, mat = v[i + 8]!;
    // Crystal keeps its own uv.x: that is the axial parameter crystalCoat reads,
    // not a packed colour.
    if (mat >= 1.6) { v[i + 7] = 1; continue; }
    const c: Rgb = mat >= 0.86 ? SKIN.glow
      : mat >= 0.45 ? SKIN.rubber
      : mat >= 0.18 ? SKIN.metal
      : SKIN.paint;
    // Plate-to-plate variation along the length, so a flank is many panels
    // rather than one field. Deterministic, and quantised into BANDS: a smooth
    // gradient just reads as a gradient, while a step reads as a seam.
    const band = Math.floor(v[i]! * 0.9) * 0.618;
    const j = ((band - Math.floor(band)) - 0.5) * 26;
    const up = Math.max(0, ny);
    // Edge wear: upward faces are scoured clean, undersides silt up.
    const wear = up * 16 - Math.max(0, -ny) * 10;
    const q = (n: number) => Math.max(4, Math.min(250, Math.round(n + j + wear)));
    v[i + 6] = mat >= 0.86 ? PACK(SKIN.glow) : PACK([q(c[0]), q(c[1]), q(c[2])]);
    // AO: dark toward the keel and on anything facing down. This is what gives
    // a module a top and a bottom when the sun is spent and the ambient is a
    // dome that lights every direction the same.
    const h = (y + hy) / (2 * hy);
    v[i + 7] = mat >= 0.86 ? 1
      : Math.max(0.32, Math.min(1, 0.52 + h * 0.42 + up * 0.16));
  }
  return d;
}

/** §6.2, in metres. These must match the Fusion user parameters. */
export const HAB = {
  // The INTERFACE never scales. A Ø2000 clear passage is a person, and a person
  // does not scale (§6.1) — scale the whole kit by 1.8 and the crew are hobbits.
  PASSAGE: 2.0,
  COLLAR_OD: 2.6,
  COLLAR_DEPTH: 0.45,
  SHOULDER: 1.6,
  LIP: 0.25,
  DETAIL_MIN: 0.15,

  /**
   * THE ENVELOPE, which is what grows.
   *
   * §6.2 sets the pod at 12 m, but §0.5 is explicit that this was a compromise:
   * the concept sheet's own hero render puts it "somewhere between 14 and 22 m",
   * and 12 was chosen "in exchange for the whole base fitting inside a 67 m
   * frame". In the water at gameplay framing 12 m did not out-mass the boat
   * enough to read as a BUILDING — it read as a slightly larger vehicle, which
   * is the exact failure §6.1 opens on.
   *
   * 16 m is inside the sheet's range and still fits: the stage-2 base goes from
   * 29 m to 37 m, which is 55 % of a 67 m frame rather than 43 %.
   */
  CORE_LEN: 16.0,
  CORE_HY: 3.75,
  CORE_HZ: 3.6,
  MOD_HY: 3.0,
  MOD_HZ: 2.7,
  // Tubes stay slim. §6.1: they are nearly all passage, and reading visibly
  // slimmer than the modules they join is the contrast that makes the modules
  // read as buildings rather than as more pipe.
  TUBE_OD: 2.5,
  TUBE_ENV: 3.2,
  TUBE_LEN: 3.5,
  NODE: 4.8,
  NODE_HY: 2.4,
  NODE_HZ: 2.15,
  DECK_Y: -1.0,

  /** Length along X of each module, so the kit and the layouts cannot disagree. */
  /**
   * FROM THE CAD, NOT FROM THIS FILE.
   *
   * These are measured off the Fusion kit by `bake-habitat.mjs` and pasted
   * here, because sec.2.4 makes Fusion the dimension authority and this table
   * had drifted from it by about a fifth — core was 16.0 against the CAD's
   * 12.0, store 8.0 against 6.5. That did not show while the meshes were built
   * from these numbers: everything agreed with everything else and was wrong
   * together. It shows the moment the CAD meshes arrive, because `run()` lays a
   * base out with `x += len` and a module a fifth too long leaves a gap.
   */
  LEN: {
    core: 12.00, power: 5.00, store: 6.50, refine: 7.00, extract: 6.90,
    dock: 5.40, green: 8.00, obs: 6.10, tube: 3.00, node: 4.00,
  },
} as const;

interface Sec { x: number; hy: number; hz: number; cut: number }

/**
 * cut = half-height − SHOULDER, so the octagon's own upper vertex lands ON the
 * shoulder datum at every station and every top chamfer is exactly 45°. The
 * line cannot drift, because it is not a feature — it is the section (§6.3).
 */
function cutFor(hy: number, hz: number): number {
  if (hy <= HAB.SHOULDER + 0.2) return Math.min(hy, hz) * 0.5385;
  return Math.max(0.15, Math.min(hy - HAB.SHOULDER, hy * 0.7));
}

function sec(x: number, hy: number, hz: number): Sec {
  return { x, hy, hz, cut: cutFor(hy, hz) };
}

/**
 * The section, as a BEVELLED chamfered octagon — sixteen points, not eight.
 *
 * Eight flat faces meeting at hard 90°-ish corners is what reads as "untextured
 * 3D": there is nothing at an edge for a highlight to sit on, so every facet is
 * a flat field of one value and the whole module comes out as a smooth blob.
 * A 180 mm bevel at each vertex is 5 px on desktop and it is the single
 * cheapest thing that makes a surface look MANUFACTURED, because each bevel
 * catches the light at a different angle from both faces it joins.
 *
 * Returned as [z, y]: the cross-section lives in the ZY plane (§4).
 */
function ring(s: Sec): [number, number][] {
  const cz = Math.min(s.cut, s.hz * 0.7);
  const cy = Math.min(s.cut, s.hy * 0.7);
  const { hy, hz } = s;
  const corners: [number, number][] = [
    [hz - cz, hy], [hz, hy - cy], [hz, -(hy - cy)], [hz - cz, -hy],
    [-(hz - cz), -hy], [-hz, -(hy - cy)], [-hz, hy - cy], [-(hz - cz), hy],
  ];
  // Cut each corner back along both of its edges. b scales with the section so
  // a small module is not all bevel.
  const b = Math.min(0.18, Math.min(hy, hz) * 0.09);
  const out: [number, number][] = [];
  for (let i = 0; i < 8; i++) {
    const prev = corners[(i + 7) % 8]!, here = corners[i]!, next = corners[(i + 1) % 8]!;
    const inset = (from: [number, number]) => {
      const dz = from[0] - here[0], dy = from[1] - here[1];
      const l = Math.hypot(dz, dy) || 1;
      const t = Math.min(b, l * 0.4) / l;
      return [here[0] + dz * t, here[1] + dy * t] as [number, number];
    };
    out.push(inset(prev), inset(next));
  }
  return out;
}

/**
 * Hard-faceted loft along X through chamfered-octagon sections.
 *
 * `revolveX` interpolates its normals around the ring, which gives a faceted
 * OUTLINE with smooth shading across it — that reads as a squashed tube rather
 * than a chamfered box (§6.3). Duplicating the ring vertices per face is what
 * makes the chamfer read, and it is most of the sheet's character.
 */
function prismX(mb: MeshBuilder, secs: Sec[], hyMax: number) {
  for (let i = 0; i < secs.length - 1; i++) {
    const a = secs[i]!, b = secs[i + 1]!;
    const ra = ring(a), rb = ring(b);
    const n = ra.length;
    for (let e = 0; e < n; e++) {
      const f = (e + 1) % n;
      const p0 = ra[e]!, p1 = ra[f]!, q0 = rb[e]!, q1 = rb[f]!;
      // Face normal from the section edge, flat in X: these are ruled spans.
      const dz = p1[0] - p0[0], dy = p1[1] - p0[1];
      const l = Math.hypot(dz, dy) || 1;
      const nz = dy / l, ny = -dz / l;
      const uv = (y: number) => (y + hyMax) / (2 * hyMax);
      const i0 = mb.vertex(a.x, p0[1], p0[0], 0, ny, nz, uv(p0[1]), 0);
      const i1 = mb.vertex(a.x, p1[1], p1[0], 0, ny, nz, uv(p1[1]), 0);
      const i2 = mb.vertex(b.x, q1[1], q1[0], 0, ny, nz, uv(q1[1]), 0);
      const i3 = mb.vertex(b.x, q0[1], q0[0], 0, ny, nz, uv(q0[1]), 0);
      mb.quad(i0, i1, i2, i3);
    }
  }
}

/** Flat end cap on an octagon section, facing ±X. */
function capX(mb: MeshBuilder, s: Sec, dir: number, hyMax: number) {
  const r = ring(s);
  const c = mb.vertex(s.x, 0, 0, dir, 0, 0, 0.5, 0);
  const base = mb.vertexCount;
  for (const [z, y] of r) mb.vertex(s.x, y, z, dir, 0, 0, (y + hyMax) / (2 * hyMax), 0);
  for (let i = 0; i < r.length; i++) {
    const a = base + i, b = base + ((i + 1) % r.length);
    if (dir > 0) mb.tri(c, a, b); else mb.tri(c, b, a);
  }
}

/**
 * The universal collar (§5), at `x` and projecting `dir`.
 *
 * §2.6 budgets 250 triangles for one of these built into a module face, so the
 * three-layer idea survives but the circular pattern does not: a collar whose
 * axis is X is seen EDGE ON (§2.1). What reads is the profile — the step from
 * fascia to ring — plus the alignment key on the top silhouette.
 */
function collarX(mb: MeshBuilder, x: number, dir: number) {
  const d = HAB.COLLAR_DEPTH;
  mb.material(MAT.metal);
  // Layer 3, the fascia: a bevelled octagon plate, stepped so its OUTLINE reads
  // — a collar on the X axis is seen edge-on, so the profile is all there is.
  const f0: Sec = { x, hy: 1.34, hz: 1.34, cut: 0.72 };
  const f1: Sec = { x: x + dir * d * 0.30, hy: 1.34, hz: 1.34, cut: 0.72 };
  const f2: Sec = { x: x + dir * d * 0.40, hy: 1.20, hz: 1.20, cut: 0.62 };
  prismX(mb, [f0, f1], 3.0);
  prismX(mb, [f1, f2], 3.0);
  capX(mb, f2, dir, 3.0);
  // Layer 1, the structural ring, and Layer 2's six locking segments standing
  // proud of it. SIX, not eight: three of six are visible from the camera
  // instead of three of eight looking accidental (§5.2).
  mb.transform(x + dir * d * 0.72, 0, 0, 0, 0, Math.PI / 2);
  cylinderY(mb, 1.13, 1.13, d * 0.58, 12, false);
  mb.popTransform();
  mb.transform(x + dir * d * 1.02, 0, 0, 0, 0, Math.PI / 2);
  cylinderY(mb, 1.02, 1.02, d * 0.1, 12, true);
  mb.popTransform();
  for (let i = 0; i < 6; i++) {
    const a = (i / 6) * Math.PI * 2 + Math.PI / 12;
    const cs = Math.cos(a), sn = Math.sin(a);
    mb.transform(x + dir * d * 0.78, cs * 1.2, sn * 1.2, a, 0, 0);
    box(mb, d * 0.22, 0.11, 0.24);
    mb.popTransform();
    // Four capture blocks at 250 mm on the fascia. This REPLACES the concept
    // sheet's "Bolt Circle Ø2300 (8x M12)": an M12 head is a third of a pixel,
    // eight times over. Four things you can see beat eight you cannot (§5.2).
    if (i % 2 === 0) {
      mb.transform(x + dir * d * 0.16, cs * 1.24, sn * 1.24, a, 0, 0);
      box(mb, d * 0.14, 0.13, 0.13);
      mb.popTransform();
    }
  }
  // ONE alignment key at top dead centre. It kills rotational ambiguity and
  // tells the player which way up a module goes before they read anything else.
  mb.transform(x + dir * d * 0.3, 1.44, 0, 0, 0, 0);
  box(mb, d * 0.34, 0.12, 0.2);
  mb.popTransform();
  // The lit passage behind the ring: what actually reads at 63 px is a dark
  // ring around something bright, so give it the bright part.
  mb.material(MAT.glow);
  mb.transform(x + dir * d * 0.62, 0, 0, 0, 0, Math.PI / 2);
  cylinderY(mb, 1.06, 1.06, d * 0.34, 14, false);
  mb.popTransform();
}

/** A raised bezel around a capsule window. Glass set into a frame, not painted on. */
function bezel(mb: MeshBuilder, x0: number, x1: number, yc: number, h: number, z: number) {
  mb.material(MAT.metal);
  const w = 0.14;
  for (const sy of [1, -1]) {
    mb.transform((x0 + x1) / 2, yc + sy * (h + w * 0.5), z - 0.03, 0, 0, 0);
    box(mb, (x1 - x0) / 2 - h * 0.35, w * 0.5, 0.07);
    mb.popTransform();
  }
  for (const [sx, ex] of [[x0, 1], [x1, -1]] as const) {
    mb.transform(sx + ex * h * 0.4, yc, z - 0.03, 0, 0, 0);
    box(mb, w * 0.55, h * 0.86, 0.07);
    mb.popTransform();
  }
}

/** Stadium-shaped pane in the XY plane at `z`, facing +Z. The window language. */
function capsulePane(mb: MeshBuilder, x0: number, x1: number, yc: number, h: number, z: number) {
  const r = h;
  const a = x0 + r, b = x1 - r;
  const pts: [number, number][] = [];
  const N = 6;
  for (let i = 0; i <= N; i++) {
    const t = (i / N) * Math.PI - Math.PI / 2;
    pts.push([b + Math.cos(t) * r, yc + Math.sin(t) * r]);
  }
  for (let i = 0; i <= N; i++) {
    const t = (i / N) * Math.PI + Math.PI / 2;
    pts.push([a + Math.cos(t) * r, yc + Math.sin(t) * r]);
  }
  // uv.x is the AXIAL parameter `crystalCoat` reads — 0 at the sill, 1 at the
  // head. The minerals use it for how far up a crystal a vertex is; a window
  // wants the same thing, so the interior tint and the dispersion run up the
  // pane instead of being flat across it.
  const ax = (py: number) => (py - yc + h) / (2 * h);
  const c = mb.vertex((x0 + x1) / 2, yc, z, 0, 0, 1, 0.5, 0);
  const base = mb.vertexCount;
  for (const [px, py] of pts) mb.vertex(px, py, z, 0, 0, 1, ax(py), 0);
  for (let i = 0; i < pts.length; i++) mb.tri(c, base + i, base + ((i + 1) % pts.length));
}

/** The 250 mm proud shoulder ledge, both flanks. The kit's signature line. */
function shoulder(mb: MeshBuilder, x0: number, x1: number, hz: number) {
  mb.material(MAT.metal);
  for (const s of [1, -1]) {
    mb.transform((x0 + x1) / 2, HAB.SHOULDER, s * (hz + HAB.LIP * 0.5), 0, 0, 0);
    box(mb, (x1 - x0) / 2, 0.15, HAB.LIP * 0.5);
    mb.popTransform();
  }
  // The shoulder is the kit's signature line (§6.3). Lighting it is what makes
  // a row of six different modules read as one product family from across the
  // frame, and it is the cheapest legibility there is at 63 px.
  strip(mb, x0 + 0.2, x1 - 0.2, HAB.SHOULDER - 0.02, hz + HAB.LIP + 0.03, 0.07);
}

/** Standard hull: collar face -> flare -> full envelope -> flare -> collar face. */
function hullSecs(len: number, hy: number, hz: number, ends: [boolean, boolean]): Sec[] {
  const b0 = ends[0] ? HAB.COLLAR_DEPTH : 0;
  const b1 = len - (ends[1] ? HAB.COLLAR_DEPTH : 0);
  const t = Math.min(1.85, (b1 - b0) * 0.3);
  const out: Sec[] = [];
  if (ends[0]) {
    out.push(sec(b0, 1.3, 1.3), sec(b0 + t * 0.3, hy * 0.73, hz * 0.73),
             sec(b0 + t * 0.73, hy * 0.967, hz * 0.95), sec(b0 + t, hy, hz));
  } else out.push(sec(b0, hy, hz));
  if (ends[1]) {
    out.push(sec(b1 - t, hy, hz), sec(b1 - t * 0.73, hy * 0.967, hz * 0.95),
             sec(b1 - t * 0.3, hy * 0.73, hz * 0.73), sec(b1, 1.3, 1.3));
  } else out.push(sec(b1, hy, hz));
  return out;
}

function standardHull(
  mb: MeshBuilder, len: number, hy: number, hz: number,
  ends: [boolean, boolean] = [true, true],
) {
  const secs = hullSecs(len, hy, hz, ends);
  mb.material(MAT.paint);
  prismX(mb, secs, hy);
  if (!ends[0]) capX(mb, secs[0]!, -1, hy);
  if (!ends[1]) capX(mb, secs[secs.length - 1]!, 1, hy);
  const t = Math.min(1.85, (len - (ends[0] ? HAB.COLLAR_DEPTH : 0)
    - (ends[1] ? HAB.COLLAR_DEPTH : 0)) * 0.3);
  const s0 = (ends[0] ? HAB.COLLAR_DEPTH : 0) + t, s1 = len - (ends[1] ? HAB.COLLAR_DEPTH : 0) - t;
  shoulder(mb, s0 - t * 0.6, s1 + t * 0.6, hz);
  if (s1 - s0 > 1.6) ribs(mb, s0 + 0.3, s1 - 0.3, hy, hz, Math.max(2, Math.round((s1 - s0) / 2.6)));

  // PLATING. Longitudinal stringers layered onto the flats, so the surface is
  // plates on plates rather than one continuous field of a single value.
  const cy = Math.min(cutFor(hy, hz), hy * 0.7);
  const roofW = (hz - cy) * 0.62;
  stringer(mb, s0 - t * 0.5, s1 + t * 0.5, hy + 0.05, 0, 0.05, roofW);      // roof plate
  stringer(mb, s0 - t * 0.3, s1 + t * 0.3, hy - cy * 0.55, hz * 0.72 + 0.05, 0.07, 0.05); // upper chine
  stringer(mb, s0 - t * 0.3, s1 + t * 0.3, -hy + cy * 0.5, hz + 0.05, 0.07, 0.05);        // lower chine
  // ACCENT STRIPS ON THE EDGES. The chamfer edges are where a cyan line reads
  // hardest, because the two faces either side of it fall away at different
  // angles — the strip stays bright while both faces shade. Camera side only:
  // the -Z half is never rasterised (§2.1).
  const cE = Math.min(cutFor(hy, hz), hy * 0.7);
  strip(mb, s0 - t * 0.5, s1 + t * 0.5, hy - cE * 0.45, hz - cE * 0.42 + 0.06, 0.06);
  strip(mb, s0 - t * 0.5, s1 + t * 0.5, -hy + cE * 0.45, hz - cE * 0.42 + 0.06, 0.06);
  // NO VERTICAL BARS ACROSS THE FLANK. The first version put short glowing
  // uprights near each end to break the run into tiers; over a window they read
  // as CELL BARS, which is not the look. Tiering is the roof plate's job.

  // Serviceable machinery, on the LOWER-FAR quadrant. §2.1: the underside is
  // never seen as a surface, only where it breaks the lower silhouette — and
  // the far side is never rasterised at all, so this is the cheap place to put
  // the busy stuff that makes the profile read as engineered.
  greebles(mb, s0, s1, -hy * 0.55, -hz * 0.86, 1337 + Math.round(len * 7), Math.max(4, Math.round((s1 - s0) / 1.1)), hy / 3);
  greebles(mb, s0, s1, hy * 0.86, -hz * 0.45, 909 + Math.round(hy * 31), Math.max(3, Math.round((s1 - s0) / 1.6)), hy / 3.4);
  // Roof hardware. §2.1: the top is seen at up to 20° of tilt and it is the only
  // horizontal surface the player ever gets any of, so it is worth dressing.
  greebles(mb, s0 + 0.3, s1 - 0.3, hy + 0.14, hz * 0.3, 77 + Math.round(len * 13),
    Math.max(3, Math.round((s1 - s0) / 1.9)), hy / 3.6);
  if (ends[0]) collarX(mb, HAB.COLLAR_DEPTH, -1);
  if (ends[1]) collarX(mb, len - HAB.COLLAR_DEPTH, 1);
  return secs;
}

/** Same section, grown by `d` all round. Keeps `cut`, so a band stays parallel. */
function grow(s: Sec, x: number, d: number): Sec {
  return { x, hy: s.hy + d, hz: s.hz + d, cut: s.cut };
}

/**
 * Transverse structural bands.
 *
 * The first pass in the water came back "the shading is ugly", and it was: a
 * chamfered octagon with nothing on it is a smooth pale blob, and this renderer
 * has no per-object normal maps to rescue it — it has three shared greyscale
 * detail textures sampled in WORLD space, which slide across a mesh rather than
 * belonging to it. So surface interest has to be GEOMETRY, and at 60–110 mm per
 * pixel a band has to stand ≥ 150 mm proud to exist at all (§2.2).
 *
 * Pitch is kept over REPEAT_MIN 600 mm, or the bands alias into a flat grey.
 */
function ribs(mb: MeshBuilder, x0: number, x1: number, hy: number, hz: number, n: number) {
  const base = sec(0, hy, hz);
  mb.material(MAT.metal);
  for (let i = 0; i < n; i++) {
    const x = x0 + ((i + 0.5) / n) * (x1 - x0);
    prismX(mb, [grow(base, x - 0.11, 0.16), grow(base, x + 0.11, 0.16)], hy);
  }
}

/**
 * A running light strip. §2.7: lights are capped at 48 and dropped silently
 * past that, so integrated lighting is EMISSIVE GEOMETRY, never a light per
 * strip. Cyan because it is the accent that survives — extinction kills red
 * first, so an amber strip at −2500 m is a grey strip (§6.5).
 */
function strip(mb: MeshBuilder, x0: number, x1: number, y: number, z: number, h = 0.11) {
  // 0.09 DEEP, and standing clear of the skin. At 0.03-0.05 proud these were
  // near-coplanar with the surface they light, and near-coplanar under TAA is a
  // flicker: the resolve keeps swapping which of the two won the depth test.
  // Emissive geometry has to be a THING standing off the hull, not a decal.
  mb.material(MAT.glow);
  mb.transform((x0 + x1) / 2, y, z + 0.06, 0, 0, 0);
  box(mb, (x1 - x0) / 2, h, 0.09);
  mb.popTransform();
}

/** A raised longitudinal plate. Layered plating is what "slick" is made of. */
function stringer(mb: MeshBuilder, x0: number, x1: number, y: number, z: number,
                  hy: number, hz: number, mat = MAT.metal) {
  mb.material(mat);
  mb.transform((x0 + x1) / 2, y, z, 0, 0, 0);
  box(mb, (x1 - x0) / 2, hy, hz);
  mb.popTransform();
}

/**
 * Deterministic hardware: pipe runs, valve blocks, vents.
 *
 * §1's thesis is "a disciplined outer surface with visible, serviceable
 * machinery", and the first pass had the discipline and none of the machinery.
 * Seeded rather than random so a module is the same object every run — and so a
 * screenshot can be compared with the last one.
 */
function greebles(mb: MeshBuilder, x0: number, x1: number, y: number, z: number,
                  seed: number, n: number, scale = 1) {
  let r = seed;
  const rnd = () => (r = (r * 1103515245 + 12345) & 0x7fffffff) / 0x7fffffff;
  for (let i = 0; i < n; i++) {
    const t = (i + 0.5) / n;
    const x = x0 + t * (x1 - x0);
    const k = rnd();
    mb.material(k > 0.82 ? MAT.glow : MAT.metal);
    if (k < 0.34) {                                   // a pipe run along the flank
      mb.transform(x, y + (rnd() - 0.5) * 0.3 * scale, z, 0, 0, Math.PI / 2);
      cylinderY(mb, 0.09 * scale, 0.09 * scale, (x1 - x0) / n * 0.9, 5, false);
    } else if (k < 0.7) {                             // a valve block
      mb.transform(x, y + (rnd() - 0.5) * 0.4 * scale, z + 0.06 * scale, 0, 0, 0);
      box(mb, 0.16 * scale, (0.1 + rnd() * 0.14) * scale, 0.1 * scale);
    } else if (k < 0.82) {                            // a louvred vent
      mb.transform(x, y + (rnd() - 0.5) * 0.35 * scale, z + 0.04 * scale, 0, 0, 0);
      box(mb, 0.22 * scale, 0.13 * scale, 0.05 * scale);
    } else {                                          // a marker lamp
      mb.transform(x, y + (rnd() - 0.5) * 0.3 * scale, z + 0.05 * scale, 0, 0, 0);
      box(mb, 0.07 * scale, 0.07 * scale, 0.05 * scale);
    }
    mb.popTransform();
  }
}

/**
 * THE GLAZING, AS ITS OWN MESH — and this is the change that matters most.
 *
 * The revised visual target is warm-lit interiors seen through big cyan-framed
 * glass, and that CANNOT be one mesh: `emissive` is per INSTANCE (§2.3), so a
 * module drawn with cyan accent strips has no way to also carry a 2700 K
 * interior. §7 already saw this and registered `hab_glass` as a separate
 * translucent slot for exactly this reason.
 *
 * So each module gets a companion mesh queued at the same transform with a warm
 * emissive. Inside it:
 *   - MAT.glow surfaces are the lit interior — they take the warm emissive;
 *   - MAT.metal surfaces are furniture, racks and planting, which stay DARK and
 *     read as silhouettes against it. That contrast is what makes a window look
 *     like a room rather than a lamp.
 */
function room(mb: MeshBuilder, x0: number, x1: number, yc: number, h: number, z: number) {
  /**
   * A RELIEF JUST BEHIND THE PANE, NOT A ROOM 1.35 m INSIDE THE HULL.
   *
   * It was the latter, and it was invisible. `prismX` lofts a CLOSED hull and
   * `bezel` only lays a metal frame on the outside of it — there is no aperture
   * anywhere in the kit. So a room built at `hz - 1.35` sat inside solid
   * geometry, was occluded by the hull's own near face in every frame, and had
   * never once been drawn. Every "lit window" in the base was the pane's
   * emissive and nothing else, and this was several hundred triangles a module
   * of furniture nobody could ever see.
   *
   * Cutting a real opening means splitting the flank quads of a ruled loft
   * across seven modules, and the hull is also the collision hull. The cheaper
   * and older answer is the one used for every lit window in every game that
   * does not model interiors: put the interior in the gap between the hull
   * flat and the glass, and let it read as depth.
   *
   * `z` is the PANE's plane. The visible slot is whatever lies between the
   * hull flat and the glass — about 90 mm — so everything below is built
   * backwards from the pane in millimetres rather than in metres. On a window
   * about forty pixels tall, 90 mm of parallax and 1.35 m of it are the same
   * picture; the difference is that this one is on screen.
   */
  const back = z - 0.075;
  mb.material(MAT.glow);
  // The lit volume, and DELIBERATELY not the full height of the window. A slab
  // filling the opening reads as a lamp; leaving dark above and below it reads
  // as a room with a ceiling, which is the whole difference.
  mb.transform((x0 + x1) / 2, yc - h * 0.15, back, 0, 0, 0);
  box(mb, (x1 - x0) / 2 - 0.15, h * 0.6, 0.008);
  mb.popTransform();
  mb.transform((x0 + x1) / 2, yc - h, back + 0.030, 0, 0, 0);   // lit floor
  box(mb, (x1 - x0) / 2, 0.04, 0.030);
  mb.popTransform();
  // Dark furniture against the light: a deck line, consoles, racks, planting.
  mb.material(MAT.metal);
  mb.transform((x0 + x1) / 2, yc - h * 0.34, back + 0.034, 0, 0, 0);
  box(mb, (x1 - x0) / 2, 0.05, 0.032);
  mb.popTransform();
  let r = 20250825;
  const rnd = () => (r = (r * 1103515245 + 12345) & 0x7fffffff) / 0x7fffffff;
  const n = Math.max(3, Math.round((x1 - x0) / 1.05));
  for (let i = 0; i < n; i++) {
    const x = x0 + ((i + 0.5) / n) * (x1 - x0);
    const k = rnd();
    const hh = (0.2 + rnd() * 0.4) * h;
    mb.transform(x, yc - h * 0.34 + hh + 0.05, back + 0.030 + (rnd() - 0.5) * 0.020, 0, 0, 0);
    box(mb, (0.1 + rnd() * 0.16) * (x1 - x0) / n, hh, 0.014 + k * 0.013);
    mb.popTransform();
  }
}

/**
 * Glazing for a standard module, IN TWO PIECES, and the split is the whole
 * reason a window can be seen through.
 *
 * This used to build the room and the pane into ONE mesh, which forced the
 * whole thing into the opaque pass: put that slot in the translucent pass and
 * its two surfaces — the pane, and the lit room 1.3 m behind it — get sorted
 * against each other every frame, the order flips as the camera moves, and the
 * window appears to slide up and down. That bug is why the glazing was made
 * opaque in the first place, and making it translucent again unchanged would
 * bring it straight back.
 *
 * So they are separated by what they physically are. The room is solid: walls,
 * a floor, consoles, all of it opaque, all of it writing depth in the opaque
 * pass like any other geometry. The pane is a surface over the top of it, drawn
 * afterwards in the translucent pass and blended.
 *
 * The translucent slot then holds ONE surface and has nothing to sort against
 * itself, so the failure is not fixed but unreachable — and the room is behind
 * real glass instead of behind a picture of it.
 */
function glazing(part: 'room' | 'pane',
                 L: number, hy: number, hz: number, x0f: number, x1f: number,
                 ycf: number, hf: number, skylight = false): MeshData {
  const mb = new MeshBuilder();
  const x0 = L * x0f, x1 = L * x1f, yc = hy * ycf, h = hy * hf;
  if (part === 'room') {
    // The same plane `capsulePane` glazes, so the relief sits directly behind
    // the glass rather than behind the hull.
    room(mb, x0 + 0.3, x1 - 0.3, yc, h * 0.92, hz + 0.09);
    return skin(mb.build(), hy);
  }
  /**
   * THE PANE IS SILICA, NOT A GLOWING PLATE.
   *
   * `MAT.crystal` is the one material in this shader that actually behaves like
   * glass: `wCrystal` refuses the detail layer outright — "a cut face is the one
   * surface in this game that genuinely has no grain" — and `wGem` opens the
   * gem terms, which are an interior, dispersion and translucency. That is a
   * window. `MAT.glow` is a lamp with a pane painted on it.
   *
   * §2.3 lists `crystal` as spoken for and not to be borrowed, and the reason it
   * gives is CLASSIFICATION: the slot exists so iron does not get a third of the
   * gem treatment. Window glass is not a fraction of a gem — it is the same
   * optical thing the slot was cut for, so this is the slot working as intended
   * rather than a borrow.
   */
  mb.material(MAT.crystal);
  capsulePane(mb, x0, x1, yc, h, hz + 0.09);
  if (skylight) {
    // Roof glass. "Fewer small isolated windows, more wrap-around glass" — a
    // skylight is also the one piece of glazing the 20° tilt actually looks INTO.
    const cy = Math.min(cutFor(hy, hz), hy * 0.7);
    const w = (hz - cy) * 0.5;
    const b = mb.vertexCount;
    mb.vertex(L * 0.3, hy + 0.03, -w, 0, 1, 0, 0.15, 0);
    mb.vertex(L * 0.7, hy + 0.03, -w, 0, 1, 0, 0.15, 0);
    mb.vertex(L * 0.7, hy + 0.03, w, 0, 1, 0, 0.9, 0);
    mb.vertex(L * 0.3, hy + 0.03, w, 0, 1, 0, 0.9, 0);
    mb.quad(b, b + 1, b + 2, b + 3);
  }
  return skin(mb.build(), hy);
}

// ---------------------------------------------------------------------------
// The catalog. Every module is built with its own origin at x = 0, keel-centred
// on the passage centreline, so a base composition is a list of translations.
// ---------------------------------------------------------------------------

/** 03 Habitat pod. The hero: 12 x 6 x 6, collars at ±X and one dropping to −Y. */
export function buildHabCore(): MeshData {
  const mb = new MeshBuilder();
  const L = HAB.CORE_LEN, hy = HAB.CORE_HY, hz = HAB.CORE_HZ;
  standardHull(mb, L, hy, hz);
  // The drop to the dock (§0.5): a collar under the keel at mid-length.
  mb.material(MAT.metal);
  mb.transform(L / 2, -hy - 0.22, 0, 0, 0, Math.PI / 2);
  cylinderY(mb, 1.3, 1.15, 0.45, 8, false);
  mb.popTransform();
  // Panoramic window, CAMERA-FACING SIDE ONLY — there is only one side. One
  // clean glass capsule: mullions in front of it read as a cage (§8.1).
  bezel(mb, L * 0.19, L * 0.81, 0.0, hy * 0.47, hz + 0.02);
  // Roof spine — the only horizontal surface the player ever gets any of.
  mb.material(MAT.metal);
  mb.transform(L / 2, hy + 0.12, 0, 0, 0, 0);
  box(mb, L * 0.31, 0.12, 0.7);
  mb.popTransform();
  // Three fat masts. Twelve thin ones are stipple and TAA eats them (§11).
  for (const [mf, h] of [[0.27, 1.3], [0.38, 1.8], [0.74, 1.0]] as const) {
    const mx = L * mf;
    mb.transform(mx, hy + 0.24 + h / 2, 0, 0, 0, 0);
    cylinderY(mb, 0.125, 0.105, h, 6, false);
    mb.popTransform();
  }
  // Lower rail as a PROFILE break on the near-lower chamfer, not a modelled tray.
  mb.transform(L / 2, -hy * 0.62, hz - hy * 0.38, Math.PI / 4, 0, 0);
  box(mb, L * 0.31, 0.22, 0.22);
  mb.popTransform();
  return skin(mb.build(), hy);
}

/** 02 Power module: denser and more technical, and the only amber in the kit. */
export function buildHabPower(): MeshData {
  const mb = new MeshBuilder();
  const L = HAB.LEN.power, hy = HAB.MOD_HY, hz = HAB.MOD_HZ;
  standardHull(mb, L, hy, hz);
  mb.material(MAT.metal);
  for (const ff of [0.38, 0.52, 0.66]) {       // roof heat-exchanger fins
    const fx = L * ff;
    mb.transform(fx, hy + 0.25, 0, 0, 0, 0);
    box(mb, 0.125, 0.25, 1.15);
    mb.popTransform();
  }
  for (const df of [0.29, 0.64]) {             // two fat cooling ducts
    const dx = L * df;
    mb.transform(dx, 0, hz - 0.06, 0, 0, 0);
    cylinderY(mb, 0.28, 0.28, 3.4, 6, false);
    mb.popTransform();
  }
  bezel(mb, L * 0.19, L * 0.76, -hy * 0.28, hy * 0.24, hz + 0.02);
  mb.transform(L / 2, hy * 0.28, hz + 0.02, 0, 0, 0); // diagnostic band: geometry, not a light
  box(mb, L * 0.28, 0.1, 0.02);
  mb.popTransform();
  return skin(mb.build(), hy);
}

/** 05 Storage: simpler and cheaper-looking, deliberately. */
export function buildHabStore(): MeshData {
  const mb = new MeshBuilder();
  const L = HAB.LEN.store, hy = HAB.MOD_HY, hz = HAB.MOD_HZ;
  standardHull(mb, L, hy, hz);
  mb.material(MAT.metal);
  for (const bf of [0.31, 0.45, 0.59, 0.73]) { // four cartridge bays
    const bx = L * bf;
    mb.transform(bx, 0, hz - 0.1, 0, 0, 0);
    box(mb, 0.4, 0.8, 0.12);
    mb.popTransform();
  }
  bezel(mb, L * 0.21, L * 0.79, hy * 0.46, hy * 0.15, hz + 0.02);
  return skin(mb.build(), hy);
}

/** 04 Refinery: machinery concentrated to ONE END along X (§8.6). */
export function buildHabRefine(): MeshData {
  const mb = new MeshBuilder();
  const L = HAB.LEN.refine, hy = HAB.MOD_HY, hz = HAB.MOD_HZ;
  standardHull(mb, L, hy, hz);
  mb.material(MAT.metal);
  mb.transform(L * 0.77, hy + 0.7, 0, 0, 0, 0);
  box(mb, L * 0.17, 1.25, 1.7);
  mb.popTransform();
  for (const sf of [0.70, 0.84]) {
    const sx = L * sf;
    mb.transform(sx, hy + 2.3, 0, 0, 0, 0);
    cylinderY(mb, 0.225, 0.2, 1.3, 6, false);
    mb.popTransform();
  }
  bezel(mb, L * 0.11, L * 0.55, hy * 0.16, hy * 0.26, hz + 0.02);
  return skin(mb.build(), hy);
}

/** 01 Mining extractor: blind at −X, head buried in an ore seam. */
export function buildHabExtract(): MeshData {
  const mb = new MeshBuilder();
  const L = HAB.LEN.extract, hy = HAB.MOD_HY, hz = HAB.MOD_HZ;
  standardHull(mb, L, hy, hz, [false, true]);
  mb.material(MAT.metal);
  mb.transform(-0.3, 0, 0, 0, 0, Math.PI / 2);   // circular excavation head
  cylinderY(mb, 1.4, 1.25, 1.2, 10);
  mb.popTransform();
  for (let i = 0; i < 3; i++) {                  // three cutter arms
    const a = (i / 3) * Math.PI * 2 + 0.5;
    mb.transform(-0.6, Math.cos(a) * 1.6, Math.sin(a) * 1.6, a, 0, 0);
    box(mb, 0.3, 0.4, 0.25);
    mb.popTransform();
  }
  bezel(mb, L * 0.20, L * 0.72, hy * 0.2, hy * 0.24, hz + 0.02);
  return skin(mb.build(), hy);
}

/** 06 Airlock / dock: +Y collar up into the base, −Y vehicle skirt (§8.10a). */
export function buildHabDock(): MeshData {
  const mb = new MeshBuilder();
  const L = HAB.LEN.dock, hy = HAB.MOD_HY, hz = HAB.MOD_HZ;
  const secs = [sec(0, 1.7, 1.7), sec(0.6, hy, hz), sec(L - 0.6, hy, hz), sec(L, 1.7, 1.7)];
  mb.material(MAT.paint);
  prismX(mb, secs, hy);
  capX(mb, secs[0]!, -1, hy);
  capX(mb, secs[3]!, 1, hy);
  shoulder(mb, 0.6, L - 0.6, hz);
  mb.material(MAT.metal);
  mb.transform(L / 2, hy + 0.22, 0, 0, 0, 0);    // the universal collar, upward
  cylinderY(mb, 1.15, 1.3, 0.45, 8, false);
  mb.popTransform();
  // VEHICLE_SKIRT_OD 1.8 with a 1.0 telescoping trunk. NOT the universal collar:
  // a 2.6 skirt would span practically the whole 2.69 m beam.
  mb.transform(L / 2, -hy - 0.35, 0, 0, 0, 0);
  cylinderY(mb, 0.9, 0.75, 0.7, 10);
  mb.popTransform();
  mb.transform(L / 2, -hy - 1.6, 0, 0, 0, 0);
  cylinderY(mb, 0.5, 0.5, 1.9, 8);
  mb.popTransform();
  mb.material(MAT.rubber);
  mb.transform(L / 2, -hy - 2.6, 0, 0, 0, 0);
  cylinderY(mb, 0.56, 0.56, 0.12, 8);
  mb.popTransform();
  bezel(mb, L * 0.15, L * 0.85, hy * 0.06, hy * 0.22, hz + 0.02);
  return skin(mb.build(), hy);
}

/** 07/08/09 tube, and 13 vertical shaft when rotated 90° about Z. */
export function buildHabTube(): MeshData {
  const mb = new MeshBuilder();
  const L = HAB.TUBE_LEN, r = HAB.TUBE_OD / 2;
  mb.material(MAT.metal);
  mb.transform(L / 2, 0, 0, 0, 0, Math.PI / 2);
  // 28, not 16. Everything else in this kit is deliberately faceted — the
  // chamfered-octagon hulls duplicate their ring vertices so the chamfer READS
  // (see `prismX`), and the dome is ten panels on purpose. A pressure pipe is
  // the exception: it is the one part of the catalogue that is supposed to be
  // round, it is the part there are most of on screen, and at 16 its 22 degree
  // facets sit right at the edge of metal's lobe. This costs 24 triangles.
  cylinderY(mb, r, r, L, 28, false);
  mb.popTransform();
  // Reinforcement ring at each section join — which is what makes instancing
  // read: three of these end to end is catalog part 09, not one long pipe (§7).
  for (const rx of [0.16, L - 0.16]) {
    mb.transform(rx, 0, 0, 0, 0, Math.PI / 2);
    cylinderY(mb, HAB.TUBE_ENV / 2, HAB.TUBE_ENV / 2, 0.32, 16, true);
    mb.popTransform();
    mb.transform(rx, 0, 0, 0, 0, Math.PI / 2);
    cylinderY(mb, r + 0.16, r + 0.16, 0.44, 16, false);
    mb.popTransform();
  }
  // Intermediate hoops. A bare cylinder between two rings is the plainest thing
  // in the kit and there are more tubes on screen than anything else.
  for (let i = 1; i <= 3; i++) {
    mb.transform((i / 4) * L, 0, 0, 0, 0, Math.PI / 2);
    cylinderY(mb, r + 0.09, r + 0.09, 0.12, 16, false);
    mb.popTransform();
  }
  // Dorsal spine: carries SHOULDER_Y unbroken across the tube, so the line runs
  // from a module's shoulder, over the tube, into the next module's (§6.2).
  mb.transform(L / 2, 1.42, 0, 0, 0, 0);
  box(mb, L / 2, 0.18, 0.3);
  mb.popTransform();
  mb.transform(L / 2, 1.2, 0, 0, 0, 0);
  box(mb, L / 2 - 0.1, 0.1, 0.46);
  mb.popTransform();
  // Utility channel on the lower-FAR quadrant: hidden in both orientations, and
  // it still breaks the lower profile (§7).
  mb.transform(L / 2, -0.98, -0.98, Math.PI / 4, 0, 0);
  box(mb, L / 2, 0.22, 0.22);
  mb.popTransform();
  greebles(mb, 0.5, L - 0.5, -0.72, -1.05, 4242, 4, 0.7);
  // Two conduit runs along the flanks. §8.12: a conduit that visibly spans two
  // modules is the cheapest possible signal that a base is one SYSTEM rather
  // than a row of objects.
  for (const sz of [1, -1]) {
    mb.transform(L / 2, 0.62, sz * (r + 0.1), 0, 0, Math.PI / 2);
    cylinderY(mb, 0.08, 0.08, L, 6, false);
    mb.popTransform();
  }
  mb.material(MAT.glow);                          // one fat strip, not many thin
  mb.transform(L / 2, 0, r + 0.11, 0, 0, 0);
  box(mb, L / 2 - 0.24, 0.12, 0.1);
  mb.popTransform();
  mb.transform(L / 2, -0.5, r + 0.1, 0, 0, 0);
  box(mb, L / 2 - 0.5, 0.05, 0.08);
  mb.popTransform();
  return skin(mb.build(), HAB.TUBE_ENV / 2);
}

function junction(px: boolean, nx: boolean, py: boolean, ny: boolean): MeshData {
  const mb = new MeshBuilder();
  const L = HAB.LEN.node, hy = HAB.NODE_HY, hz = HAB.NODE_HZ;
  standardHull(mb, L, hy, hz, [nx, px]);
  mb.material(MAT.metal);
  if (py) {
    mb.transform(L / 2, hy + 0.22, 0, 0, 0, 0);
    cylinderY(mb, 1.15, 1.3, 0.45, 8, false);
    mb.popTransform();
  }
  if (ny) {
    mb.transform(L / 2, -hy - 0.22, 0, 0, 0, 0);
    cylinderY(mb, 1.3, 1.15, 0.45, 8, false);
    mb.popTransform();
  }
  mb.material(MAT.glow);
  mb.transform(L / 2, 0.3, hz + 0.02, Math.PI / 2, 0, 0);
  cylinderY(mb, 0.6, 0.6, 0.04, 8);
  mb.popTransform();
  return skin(mb.build(), hy);
}

/** 10 vertical T. Rotated 0/90/180/270° about Z it is all four tees (§7). */
export const buildHabTee = () => junction(true, true, true, false);
/** 12 corner 90°: +X and +Y, blind outer corner at −X. */
export const buildHabElbow = () => junction(true, false, true, false);
/** 11 X-junction. */
export const buildHabCross = () => junction(true, true, true, true);

/** 14 Observation dome: ten panels. Thirty facets on an 85–115 px dome is grey. */
export function buildHabObs(): MeshData {
  const mb = new MeshBuilder();
  const N = 10;
  const prof: [number, number][] = [[0.9, 3.0], [1.8, 2.85], [2.5, 2.45], [3.05, 1.75], [3.45, 0.85], [3.6, 0.26]];
  mb.material(MAT.metal);
  mb.transform(0, 0.45, 0, 0, 0, 0);
  cylinderY(mb, 3.0, 3.0, 0.9, N, false);
  mb.popTransform();
  // Meridian ribs on the decagon fold lines, and a structural ring at mid height.
  for (let i = 0; i < N; i++) {
    const a = (i / N) * Math.PI * 2;
    for (let s = 0; s < prof.length - 1; s++) {
      const [y0, r0] = prof[s]!, [y1, r1] = prof[s + 1]!;
      mb.transform(Math.cos(a) * (r0 + r1) / 2, (y0 + y1) / 2, Math.sin(a) * (r0 + r1) / 2, 0, -a, 0);
      box(mb, 0.16, Math.hypot(y1 - y0, r1 - r0) / 2, 0.16);
      mb.popTransform();
    }
  }
  mb.transform(0, 1.82, 0, 0, 0, 0);
  cylinderY(mb, 3.05, 3.05, 0.25, N, false);
  mb.popTransform();
  // SILICA, LIKE EVERY OTHER PANE IN THE KIT. See `glazing`: `MAT.crystal` is
  // the one tag in this shader that behaves like glass, and `MAT.glow` is a
  // lamp with a pane painted on it. The dome is the most glass-forward piece
  // in the whole catalogue and it was the one still lit from inside like a
  // paper lantern.
  //
  // The vertices below already write `y / 3.6` into uv.x, which is exactly the
  // axial parameter `crystalCoat` wants — 0 at the sill, 1 at the crown — so
  // the interior tint and the dispersion run UP the dome. And `skin` leaves
  // crystal's uv.x alone rather than packing a colour into it, so this needed
  // no other change.
  mb.material(MAT.crystal);                       // the glazing itself
  for (let s = 0; s < prof.length - 1; s++) {
    const [y0, r0] = prof[s]!, [y1, r1] = prof[s + 1]!;
    const base = mb.vertexCount;
    for (let i = 0; i <= N; i++) {
      const a = (i / N) * Math.PI * 2;
      const ny = (r0 - r1), nr = (y1 - y0), l = Math.hypot(ny, nr) || 1;
      mb.vertex(Math.cos(a) * r0 * 0.96, y0, Math.sin(a) * r0 * 0.96,
        Math.cos(a) * nr / l, ny / l, Math.sin(a) * nr / l, y0 / 3.6, 0);
      mb.vertex(Math.cos(a) * r1 * 0.96, y1, Math.sin(a) * r1 * 0.96,
        Math.cos(a) * nr / l, ny / l, Math.sin(a) * nr / l, y1 / 3.6, 0);
    }
    for (let i = 0; i < N; i++) mb.quad(base + i * 2, base + i * 2 + 2, base + i * 2 + 3, base + i * 2 + 1);
  }
  return skin(mb.build(), 3.6);
}

/** 15 Greenhouse: the one warm colour in a cold world. */
export function buildHabGreen(): MeshData {
  const mb = new MeshBuilder();
  const L = HAB.LEN.green, hy = 2.55, hz = 2.35;
  standardHull(mb, L, hy, hz);
  bezel(mb, L * 0.20, L * 0.80, hy * 0.14, hy * 0.44, hz + 0.02);
  const base = mb.vertexCount;                     // roof glazing
  mb.vertex(L * 0.2, hy + 0.02, -1.1, 0, 1, 0, 1, 0);
  mb.vertex(L * 0.8, hy + 0.02, -1.1, 0, 1, 0, 1, 0);
  mb.vertex(L * 0.8, hy + 0.02, 1.1, 0, 1, 0, 1, 0);
  mb.vertex(L * 0.2, hy + 0.02, 1.1, 0, 1, 0, 1, 0);
  mb.quad(base, base + 1, base + 2, base + 3);
  return skin(mb.build(), hy);
}

/** Foundation leg, four per module, seated on `floorAt` at its own x (§8.2). */
export function buildHabLeg(): MeshData {
  const mb = new MeshBuilder();
  mb.material(MAT.metal);
  mb.transform(0, -0.15, 0, 0, 0, 0);
  box(mb, 0.3, 0.15, 0.25);
  mb.popTransform();
  mb.transform(0, -0.5, 0, 0, 0, 0);
  cylinderY(mb, 0.2, 0.2, 0.5, 6, false);
  mb.popTransform();
  mb.transform(0, -0.83, 0, 0, 0, 0);
  cylinderY(mb, 0.15, 0.15, 0.28, 6, false);
  mb.popTransform();
  mb.transform(0, -1.02, 0, 0, 0, 0);
  cylinderY(mb, 0.45, 0.4, 0.15, 8);
  mb.popTransform();
  return skin(mb.build(), 1.1);
}

/**
 * Parallax silhouette. One cheap build, no detail at all — a module in a
 * parallax layer is a silhouette and nothing else (§2.1), tinted to 0.46 of
 * albedo and defocused, so a shoulder lip there would cost triangles and change
 * no pixel.
 */
export function buildHabLo(): MeshData {
  const mb = new MeshBuilder();
  const hy = HAB.CORE_HY, hz = HAB.CORE_HZ, L = HAB.LEN.core;
  mb.material(MAT.paint);
  prismX(mb, [sec(0, hy, hz), sec(L, hy, hz)], hy);
  capX(mb, sec(0, hy, hz), -1, hy);
  capX(mb, sec(L, hy, hz), 1, hy);
  return skin(mb.build(), hy);
}


/**
 * The catalog, as the renderer needs it: a slot name, the mesh, an instance
 * capacity and the envelope the harness measures against.
 *
 * §7 reconciles 15 catalog parts to 12 meshes — 2.5D pays a dividend, because
 * rotating about Z keeps a mesh in the play plane, so one tee is four tees and
 * one tube covers parts 07/08/09 and 13. Capacities are sized for a large base
 * plus the next one visible in the parallax, and overflow is SILENT (§2.5): past
 * capacity instances are dropped and it reads as scenery vanishing.
 */
export interface HabModule {
  name: string;
  build: () => MeshData;
  capacity: number;
  /** Total length along X, and the envelope half-extents. */
  len: number;
  hy: number;
  hz: number;
  /** False for the tube, the leg and the parallax silhouette. */
  shoulder: boolean;
  /**
   * Companion glazing, queued at the same transform in its own slot so it can
   * carry a WARM emissive while the body carries cyan (§2.3, §7).
   */
  glass?: () => MeshData;
  /**
   * The lit interior behind `glass`. Opaque, its own slot, drawn in the opaque
   * pass — see `glazing` for why it is not part of the pane's mesh.
   */
  room?: () => MeshData;
}

/**
 * One argument list per module, spread into both halves of `glazing`.
 *
 * Written once because the room and the pane MUST agree: they are the same
 * opening described twice, and a figure that drifts between them puts a window
 * somewhere its room is not. Fourteen literal argument lists would have made
 * that a one-character edit away at all times.
 */
const GZ_CORE = [HAB.LEN.core, HAB.CORE_HY, HAB.CORE_HZ, 0.19, 0.81, 0, 0.47, true] as const;
const GZ_POWER = [HAB.LEN.power, HAB.MOD_HY, HAB.MOD_HZ, 0.19, 0.76, -0.28, 0.24] as const;
const GZ_STORE = [HAB.LEN.store, HAB.MOD_HY, HAB.MOD_HZ, 0.21, 0.79, 0.46, 0.15] as const;
const GZ_REFINE = [HAB.LEN.refine, HAB.MOD_HY, HAB.MOD_HZ, 0.11, 0.55, 0.16, 0.26] as const;
const GZ_EXTRACT = [HAB.LEN.extract, HAB.MOD_HY, HAB.MOD_HZ, 0.20, 0.72, 0.2, 0.24] as const;
const GZ_DOCK = [HAB.LEN.dock, HAB.MOD_HY, HAB.MOD_HZ, 0.15, 0.85, 0.06, 0.22] as const;
const GZ_GREEN = [HAB.LEN.green, 2.55, 2.35, 0.20, 0.80, 0.14, 0.44, true] as const;

export const buildCoreGlass = () => glazing('pane', ...GZ_CORE);
export const buildPowerGlass = () => glazing('pane', ...GZ_POWER);
export const buildStoreGlass = () => glazing('pane', ...GZ_STORE);
export const buildRefineGlass = () => glazing('pane', ...GZ_REFINE);
export const buildExtractGlass = () => glazing('pane', ...GZ_EXTRACT);
export const buildDockGlass = () => glazing('pane', ...GZ_DOCK);
export const buildGreenGlass = () => glazing('pane', ...GZ_GREEN);

export const buildCoreRoom = () => glazing('room', ...GZ_CORE);
export const buildPowerRoom = () => glazing('room', ...GZ_POWER);
export const buildStoreRoom = () => glazing('room', ...GZ_STORE);
export const buildRefineRoom = () => glazing('room', ...GZ_REFINE);
export const buildExtractRoom = () => glazing('room', ...GZ_EXTRACT);
export const buildDockRoom = () => glazing('room', ...GZ_DOCK);
export const buildGreenRoom = () => glazing('room', ...GZ_GREEN);

export const HAB_MODULES: HabModule[] = [
  { name: 'hab_core',    build: buildHabCore,    capacity: 12, len: HAB.LEN.core,    hy: HAB.CORE_HY, hz: HAB.CORE_HZ, shoulder: true, glass: buildCoreGlass, room: buildCoreRoom },
  { name: 'hab_power',   build: buildHabPower,   capacity: 12, len: HAB.LEN.power,   hy: HAB.MOD_HY,  hz: HAB.MOD_HZ,  shoulder: true, glass: buildPowerGlass, room: buildPowerRoom },
  { name: 'hab_store',   build: buildHabStore,   capacity: 16, len: HAB.LEN.store,   hy: HAB.MOD_HY,  hz: HAB.MOD_HZ,  shoulder: true, glass: buildStoreGlass, room: buildStoreRoom },
  { name: 'hab_refine',  build: buildHabRefine,  capacity: 8,  len: HAB.LEN.refine,  hy: HAB.MOD_HY,  hz: HAB.MOD_HZ,  shoulder: true, glass: buildRefineGlass, room: buildRefineRoom },
  { name: 'hab_extract', build: buildHabExtract, capacity: 8,  len: HAB.LEN.extract, hy: HAB.MOD_HY,  hz: HAB.MOD_HZ,  shoulder: true, glass: buildExtractGlass, room: buildExtractRoom },
  { name: 'hab_dock',    build: buildHabDock,    capacity: 16, len: HAB.LEN.dock,    hy: HAB.MOD_HY,  hz: HAB.MOD_HZ,  shoulder: true, glass: buildDockGlass, room: buildDockRoom },
  { name: 'hab_obs',     build: buildHabObs,     capacity: 8,  len: HAB.LEN.obs,     hy: 3.6,         hz: 3.05,        shoulder: false },
  { name: 'hab_green',   build: buildHabGreen,   capacity: 8,  len: HAB.LEN.green,   hy: 2.55,        hz: 2.35,        shoulder: true, glass: buildGreenGlass, room: buildGreenRoom },
  { name: 'hab_tube',    build: buildHabTube,    capacity: 96, len: HAB.LEN.tube,    hy: 1.6,         hz: 1.6,         shoulder: false },
  { name: 'hab_tee',     build: buildHabTee,     capacity: 24, len: HAB.LEN.node,    hy: HAB.NODE_HY, hz: HAB.NODE_HZ, shoulder: true },
  { name: 'hab_elbow',   build: buildHabElbow,   capacity: 24, len: HAB.LEN.node,    hy: HAB.NODE_HY, hz: HAB.NODE_HZ, shoulder: true },
  { name: 'hab_cross',   build: buildHabCross,   capacity: 12, len: HAB.LEN.node,    hy: HAB.NODE_HY, hz: HAB.NODE_HZ, shoulder: true },
  { name: 'hab_leg',     build: buildHabLeg,     capacity: 96, len: 0.6,             hy: 1.1,         hz: 0.5,         shoulder: false },
  { name: 'hab_lo',      build: buildHabLo,      capacity: 32, len: HAB.LEN.core,    hy: HAB.CORE_HY, hz: HAB.CORE_HZ, shoulder: false },
];
